import React, { useCallback, useId, useState } from 'react';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  ContentCopyOutlined as DuplicateIcon,
  DeleteOutline as DeleteIcon,
  ZoomInOutlined as ZoomInIcon,
} from '@mui/icons-material';
import { Product } from '../../../types/product.types';
import { useProductTable, UseProductTableProps } from '../ProductTable/ProductTable.hooks';
import { ProductCatalogActionPermissions } from '../ProductTable/ProductTable';
import { ActionButton } from '../../common/Table/Table.styles';
import { catalogActionIconButtonSx, catalogActionSvgIconGradientSx } from '../catalogGradients';
import { CatalogActionIconGradientDefs } from '../CatalogActionIconGradientDefs';
import ImageModal from '../../ui/ImageModal';
import ProductImagePlaceholder from '../../ui/ProductImagePlaceholder';
import { isUsableProductImage } from '../../../utils/productImage';
import {
  GridContainer,
  ProductCard,
  ImageWrapper,
  ProductThumbnail,
  CardCheckbox,
  ExpandImageButton,
  CardContent,
  CardActionsBar,
  LoadingContainer,
  EmptyStateContainer,
} from './ProductGrid.styles';

export interface ProductGridProps extends UseProductTableProps {
  loading: boolean;
  className?: string;
  actionPermissions?: ProductCatalogActionPermissions;
}

const getExpandedImageUrl = (product: Product): string | undefined => {
  const p = product as Product & { thumbnail?: string | null };
  return [product.imagePath, p.thumbnail, product.thumbnailUrl].filter(isUsableProductImage)[0];
};

const getThumbnailSrc = (product: Product): string | undefined => {
  const p = product as Product & { thumbnailUrl?: string | null };
  return [p.thumbnailUrl, product.imagePath].filter(isUsableProductImage)[0];
};

const ProductGridComponent: React.FC<ProductGridProps> = ({
  products,
  loading,
  selectedProducts,
  setSelectedProducts,
  onViewProduct,
  onEditProduct,
  onDuplicateProduct,
  onDeleteProduct,
  className,
  actionPermissions,
}) => {
  const {
    canViewRow = true,
    canEdit = true,
    canDuplicate = true,
    canDelete = true,
  } = actionPermissions ?? {};
  const {
    getStatusColor,
    renderPrice,
    handleViewProduct,
    handleEditProduct,
    handleDuplicateProduct,
    handleDeleteProduct,
    handleSelectOne,
    isSelected,
  } = useProductTable({
    products,
    selectedProducts,
    setSelectedProducts,
    onViewProduct,
    onEditProduct,
    onDuplicateProduct,
    onDeleteProduct,
  });

  const catalogIconGradientId = `catalog-action-grad-${useId().replace(/:/g, '')}`;
  const iconGradSx = catalogActionSvgIconGradientSx(catalogIconGradientId);

  const [expandedImage, setExpandedImage] = useState<{ url: string; alt: string } | null>(null);
  const handleCloseExpandedImage = useCallback(() => setExpandedImage(null), []);

  const handleCardClick = (e: React.MouseEvent, product: Product) => {
    if ((e.target as HTMLElement).closest("button, [role='button'], .MuiCheckbox-root")) {
      return;
    }
    if (!canViewRow) return;
    handleViewProduct(product.id);
  };

  const handleCheckboxChange = (productId: number) => {
    handleSelectOne(productId);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (products.length === 0) {
    return (
      <EmptyStateContainer>
        <Typography color="text.secondary" variant="body1" fontWeight={600}>
          No se encontraron productos
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
          Ajuste los filtros o agregue un nuevo producto.
        </Typography>
      </EmptyStateContainer>
    );
  }

  return (
    <>
      <GridContainer className={className}>
        <CatalogActionIconGradientDefs id={catalogIconGradientId} />
        {products.map((product) => {
          const selected = isSelected(product.id);
          const expandedUrl = getExpandedImageUrl(product);
          const thumbnailSrc = getThumbnailSrc(product);
          const canExpandImage = Boolean(expandedUrl);

          return (
            <ProductCard
              key={product.id}
              onClick={(e) => handleCardClick(e, product)}
              sx={{
                outline: selected
                  ? `2px solid ${getStatusColor(product.status) === 'success' ? '#16a34a' : '#dc2626'}`
                  : undefined,
              }}
            >
              <ImageWrapper>
                <CardCheckbox
                  size="small"
                  checked={selected}
                  onChange={() => handleCheckboxChange(product.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                {canExpandImage && (
                  <Tooltip title="Ver imagen completa" arrow>
                    <ExpandImageButton
                      size="small"
                      aria-label={`Ver imagen completa de ${product.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (expandedUrl) {
                          setExpandedImage({ url: expandedUrl, alt: product.name });
                        }
                      }}
                    >
                      <ZoomInIcon sx={{ fontSize: 18 }} />
                    </ExpandImageButton>
                  </Tooltip>
                )}
                {thumbnailSrc ? (
                  <ProductThumbnail src={thumbnailSrc} alt={product.name} loading="lazy" />
                ) : (
                  <ProductImagePlaceholder variant="fill" />
                )}
              </ImageWrapper>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} noWrap title={product.name}>
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap title={product.code}>
                  {product.code}
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mt: 0.25 }}>
                  {renderPrice(product.price)}
                </Typography>
              </CardContent>
              <CardActionsBar onClick={(e) => e.stopPropagation()}>
                {canViewRow && (
                  <Tooltip title="Ver detalles" arrow>
                    <ActionButton
                      size="small"
                      onClick={() => handleViewProduct(product.id)}
                      sx={catalogActionIconButtonSx}
                    >
                      <ViewIcon fontSize="small" sx={iconGradSx} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip title="Editar" arrow>
                    <ActionButton
                      size="small"
                      onClick={() => handleEditProduct(product.id)}
                      sx={catalogActionIconButtonSx}
                    >
                      <EditIcon fontSize="small" sx={iconGradSx} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDuplicate && (
                  <Tooltip title="Duplicar" arrow>
                    <ActionButton
                      size="small"
                      onClick={() => handleDuplicateProduct(product.id)}
                      sx={catalogActionIconButtonSx}
                    >
                      <DuplicateIcon fontSize="small" sx={iconGradSx} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Eliminar" arrow>
                    <ActionButton
                      size="small"
                      onClick={() => handleDeleteProduct(product.id)}
                      sx={catalogActionIconButtonSx}
                    >
                      <DeleteIcon fontSize="small" sx={iconGradSx} />
                    </ActionButton>
                  </Tooltip>
                )}
              </CardActionsBar>
            </ProductCard>
          );
        })}
      </GridContainer>
      <ImageModal
        isOpen={expandedImage !== null}
        onClose={handleCloseExpandedImage}
        imageUrl={expandedImage?.url ?? ''}
        altText={expandedImage?.alt ?? 'Imagen del producto'}
      />
    </>
  );
};

export default React.memo(ProductGridComponent);
